import { isSrsProgressRowDue } from '../services/reviewQueueService';
import { isCardMastered, resolveSrsNextReviewDate } from '../components/SRS/algorithm';

/** Session outcome for a single cookbook card during the active study visit. */
export const SESSION_CARD_STATUS = {
  CLEAN: 'clean',
  PENALIZED: 'penalized'
};

/**
 * Whether a snippet has any SRS history (studied at least once).
 *
 * @param {Record<string, unknown> | null | undefined} progress
 */
export function isCookbookSnippetStarted(progress) {
  if (!progress || typeof progress !== 'object') return false;
  if (Number(progress.totalReviews) > 0) return true;
  if (progress.lastReviewed) return true;
  if (progress.nextReview) return true;
  return false;
}

/**
 * Whether a studied snippet is due for review today (includes overdue).
 * Never-studied cards are not due.
 *
 * @param {Record<string, unknown> | null | undefined} progress
 * @param {Date} [now]
 */
export function isCookbookSnippetDueToday(progress, now = new Date()) {
  if (!isCookbookSnippetStarted(progress)) return false;
  return isSrsProgressRowDue(progress, now);
}

/**
 * Compact next-review label for the cookbook index — must match progress-strip
 * green / yellow / grey semantics (same due check + effective due date).
 *
 * @param {Record<string, unknown> | null | undefined} progress
 * @param {Date} [now]
 * @returns {string}
 */
export function formatCookbookIndexReviewLabel(progress, now = new Date()) {
  if (!isCookbookSnippetStarted(progress)) return 'Not started';
  if (isCookbookSnippetDueToday(progress, now)) return 'Due today';

  const due = resolveSrsNextReviewDate(progress, now);
  if (!due || Number.isNaN(due.getTime())) return 'Due today';

  const daysDiff = Math.ceil((due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  if (daysDiff <= 0) return 'Due today';
  if (daysDiff === 1) return 'Tomorrow';
  if (daysDiff <= 7) return `In ${daysDiff} days`;
  return due.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: due.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
  });
}

/**
 * CSS modifier for SRS review status pills (profile snippets, home last session).
 *
 * @param {{ is_started?: boolean, is_due_today?: boolean } | null | undefined} snippet
 */
export function cookbookSnippetSrsStatusPillVariant(snippet) {
  if (!snippet?.is_started) return 'not-started';
  if (snippet?.is_due_today) return 'due-today';
  return 'scheduled';
}

/**
 * @deprecated Use isCookbookSnippetDueToday for dot visuals.
 */
export function isCookbookSnippetDueForReview(progress, now = new Date()) {
  if (!progress || typeof progress !== 'object') return false;
  return isSrsProgressRowDue(progress, now);
}

/**
 * Resolve the color for one progress dot.
 *
 * Green = studied and not due today, yellow = due today, grey = unstarted.
 *
 * @param {object} input
 * @param {Record<string, unknown> | null | undefined} input.cardProgress
 * @param {Date} [input.now]
 * @returns {'green' | 'yellow' | 'grey'}
 */
export function studyProgressDotColor({ cardProgress, now = new Date() }) {
  if (!isCookbookSnippetStarted(cardProgress)) return 'grey';
  if (isCookbookSnippetDueToday(cardProgress, now)) return 'yellow';
  return 'green';
}

/**
 * @param {object} input
 * @param {number} input.index
 * @param {number} input.currentIndex
 * @param {Record<string, unknown> | null | undefined} input.cardProgress
 * @param {Date} [input.now]
 * @returns {'green' | 'yellow' | 'grey'}
 */
export function studyProgressDotVisualState({
  index,
  currentIndex,
  sessionStatus: _sessionStatus,
  cardProgress,
  now = new Date()
}) {
  return studyProgressDotColor({ cardProgress, now });
}

/**
 * @param {Array<string | null | undefined>} statuses
 */
export function countCleanSessionCards(statuses) {
  return (statuses || []).filter((status) => status === SESSION_CARD_STATUS.CLEAN).length;
}

/**
 * How many cookbook snippets are due for review today (yellow-dot semantics).
 * @param {Array<{ progress?: Record<string, unknown> | null }>} cards
 * @param {Date} [now]
 */
export function countCookbookDueSnippets(cards, now = new Date()) {
  if (!Array.isArray(cards)) return 0;
  return cards.reduce(
    (count, card) => (isCookbookSnippetDueToday(card?.progress, now) ? count + 1 : count),
    0
  );
}

/**
 * True when every snippet was recalled cleanly this session and none remain due today.
 *
 * @param {Array<{ progress?: Record<string, unknown> | null }>} cards
 * @param {Array<string | null | undefined>} statuses
 * @param {number} totalCards
 * @param {Date} [now]
 */
export function isCookbookSessionComplete(cards, statuses, totalCards, now = new Date()) {
  const total = Number(totalCards) || 0;
  if (total <= 0) return false;
  if (countCookbookDueSnippets(cards, now) > 0) return false;
  return countCleanSessionCards(statuses) >= total;
}

/**
 * Whether a finished study session should continue in practice mode — all due
 * snippets for today are cleared and at least one started snippet remains.
 *
 * @param {object} input
 * @param {boolean} [input.isPracticeSession]
 * @param {boolean} [input.isTestMode]
 * @param {boolean} [input.showSessionSummary]
 * @param {Array<{ progress?: Record<string, unknown> | null }>} [input.cards]
 * @param {Date} [input.now]
 */
export function shouldTransitionToPracticeAfterSessionComplete({
  isPracticeSession = false,
  isTestMode = false,
  showSessionSummary = false,
  cards = [],
  now = new Date()
} = {}) {
  if (isPracticeSession || isTestMode || !showSessionSummary) return false;
  if (!Array.isArray(cards) || cards.length === 0) return false;
  if (countCookbookDueSnippets(cards, now) > 0) return false;
  return cards.some((card) => isCookbookSnippetStarted(card?.progress));
}

/**
 * True when every progress-strip dot would render green (started and not due today).
 * Used to open cookbooks directly in practice mode when there is nothing left to review.
 *
 * @param {Array<{ progress?: Record<string, unknown> | null }>} cards
 * @param {Date} [now]
 */
export function areAllCookbookProgressDotsGreen(cards, now = new Date()) {
  if (!Array.isArray(cards) || cards.length === 0) return false;
  return cards.every(
    (card) => studyProgressDotColor({ cardProgress: card?.progress, now }) === 'green'
  );
}

/**
 * @param {Array<string | null | undefined>} statuses
 */
export function countAttemptedSessionCards(statuses) {
  return (statuses || []).filter(Boolean).length;
}

/**
 * Map session statuses to legacy session-complete payloads (`correct` / `wrong`).
 *
 * @param {Array<string | null | undefined>} statuses
 * @returns {Array<'correct' | 'wrong' | null | undefined>}
 */
export function toLegacySessionResults(statuses) {
  return (statuses || []).map((status) => {
    if (status === SESSION_CARD_STATUS.CLEAN) return 'correct';
    if (status === SESSION_CARD_STATUS.PENALIZED) return 'wrong';
    return status ?? null;
  });
}

/**
 * Apply a session penalty (Again or wrong Check). Yellow until the user passes
 * Check on their own; a completed card stays green for the rest of the visit.
 *
 * @param {Array<string | null | undefined>} statuses
 * @param {number} index
 * @returns {{ statuses: Array<string | null | undefined>, firstInteraction: boolean }}
 */
export function applySessionCardPenalty(statuses, index) {
  const next = (statuses || []).slice();
  if (next[index] === SESSION_CARD_STATUS.CLEAN) {
    return { statuses: next, firstInteraction: false };
  }
  const firstInteraction = !next[index];
  next[index] = SESSION_CARD_STATUS.PENALIZED;
  return { statuses: next, firstInteraction };
}

/**
 * Record a successful Check without using Again. Recovers a penalized card to green.
 *
 * @param {Array<string | null | undefined>} statuses
 * @param {number} index
 * @returns {{ statuses: Array<string | null | undefined>, firstInteraction: boolean, becameClean: boolean }}
 */
export function applySessionCardSuccess(statuses, index) {
  const next = (statuses || []).slice();
  const prior = next[index];
  const firstInteraction = !prior;
  next[index] = SESSION_CARD_STATUS.CLEAN;

  return {
    statuses: next,
    firstInteraction,
    becameClean: prior !== SESSION_CARD_STATUS.CLEAN
  };
}

/**
 * @param {'green' | 'yellow' | 'grey'} color
 * @param {number} index
 * @param {number} total
 * @param {boolean} [isActive]
 * @param {Record<string, unknown> | null | undefined} [cardProgress]
 */
export function studyProgressDotTitle(color, index, total, isActive = false, cardProgress = null) {
  const n = index + 1;
  const suffix = isActive ? ' (current)' : '';
  const mastered = cardProgress ? isCardMastered(cardProgress) : false;
  switch (color) {
    case 'green':
      return mastered
        ? `Card ${n} of ${total}, mastered — up to date${suffix}`
        : `Card ${n} of ${total}, up to date${suffix}`;
    case 'yellow':
      if (mastered) {
        return `Card ${n} of ${total}, mastered — due today for review${suffix}`;
      }
      return `Card ${n} of ${total}, due today${suffix}`;
    default:
      return `Card ${n} of ${total}, not started${suffix}`;
  }
}
